import { Fragment, ReactElement } from 'react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Accordion, Text } from '@frontend/design-system';
import { ACCORDION_WIDTH } from './constants';

type Props = {
  createAccordionBody: (locationId: string) => ReactElement;
  filteredLocationIds: string[];
} & Omit<React.ComponentProps<typeof Accordion>, 'children'>;

/**
 * This component renders an accordion for managing Weave Assistant settings across multiple locations.
 * It creates an accordion item for each location ID provided.
 * @param createAccordionBody - Function to create the accordion body for each location
 * @param filteredLocationIds - array of location IDs the user has filtered by, this determines which accordion items are displayed
 * @returns MultiLocationAccordionControlAccordion component
 */
export const MultiLocationAccordionControlAccordion = ({
  createAccordionBody,
  filteredLocationIds,
  ...props
}: Props) => {
  const { t } = useTranslation('settings');

  return filteredLocationIds.length > 0 ? (
    <Accordion
      chevronSize={16}
      css={{
        marginTop: theme.spacing(2),
        width: ACCORDION_WIDTH,
        '[data-active="true"], .accordion-body': {
          overflow: 'clip',
        },
      }}
      size='large'
      showBoxShadow
      variant='location'
      {...props}
    >
      {filteredLocationIds.map((locationId) => (
        <Fragment key={locationId}>{createAccordionBody(locationId)}</Fragment>
      ))}
    </Accordion>
  ) : (
    <Text
      css={{
        marginTop: theme.spacing(2),
        color: theme.colors.neutral50,
      }}
    >
      {t('You have no selected locations')}
    </Text>
  );
};
